import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { ClientsApiAdapter } from '../adapters/ClientsApiAdapter';
import { Filters } from '../models/Filters';
import { Service } from '../services/Service';

export const useClients = (filters: Filters = {}) => {
    const httpClient = createHttpClient(apiClientWH);
    const apiAdapter = new ClientsApiAdapter(httpClient);
    const service = new Service(apiAdapter);

    // 🎯 Estado local para controlar si es la primera carga real
    const [isFirstLoad, setIsFirstLoad] = useState(true);

    // 🎯 Estado para mantener datos anteriores durante búsquedas
    const [previousClients, setPreviousClients] = useState<any[]>([]);

    const {
        data,
        isLoading,
        isFetching,
        error,
        refetch,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isPending,
        isInitialLoading,
    } = useInfiniteQuery({
        queryKey: ['clients', filters],
        queryFn: ({ pageParam = 1 }) =>
            service.getPaginated({
                ...filters,
                page: pageParam,
            }),
        getNextPageParam: (lastPage) => lastPage.nextPage,
        initialPageParam: 1,
        staleTime: 2 * 60 * 1000, // 2 minutos
        gcTime: 5 * 60 * 1000, // 5 minutos
        placeholderData: (previousData) => previousData,
    });

    const clients = data?.pages.flatMap((page) => page.clients) ?? [];

    // Obtener el total del API (siempre sera el mismo en todas las paginas)
    const totalFromApi = data?.pages?.[0]?.total ?? 0;

    // 🎯 Efecto para marcar que ya no es la primera carga
    useEffect(() => {
        if (clients.length > 0 && isFirstLoad) {
            setIsFirstLoad(false);
        }
    }, [clients.length, isFirstLoad]);

    // 🎯 Efecto para mantener datos anteriores durante búsquedas
    useEffect(() => {
        if (clients.length > 0) {
            // Solo actualizar si hay datos nuevos
            setPreviousClients(clients);
        }
    }, [clients.length]); // 🎯 FIX: Remover 'clients' de las dependencias para evitar bucle infinito

    // 🎯 Lógica para mantener datos durante búsquedas
    const displayClients = isFetching && !isFirstLoad && previousClients.length > 0
        ? previousClients
        : clients;

    return {
        clients: displayClients,
        // 🎯 FIX: Solo mostrar spinner en la primera carga real del componente
        isLoading: isFirstLoad && isInitialLoading,
        isFetching,
        error,
        refetch,
        // Nuevas props para scroll infinito
        fetchNextPage,
        hasNextPage: hasNextPage ?? false,
        isFetchingNextPage,
        // Stats útiles
        totalPages: data?.pages.length ?? 0,
        totalClients: displayClients.length,
        total: totalFromApi, // Total real del API
    };
};

